/**
 * Authentication Service
 *
 * Handles all authentication-related operations including:
 * - User signup and login
 * - JWT token generation and validation
 * - Password hashing and verification
 * - Session management
 * - User profile updates
 *
 * This service runs in the browser and communicates with Netlify Functions
 * for secure server-side operations.
 */

import logger from '../utils/logger.js';

class AuthService {
  constructor() {
    // Use local functions for development, production API for live
    const hostname =
      (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    const port = (typeof window !== 'undefined' && window.location && window.location.port) || '';

    const isDevelopment =
      process.env.NODE_ENV === 'development' ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      port === '8888';

    this.baseUrl = isDevelopment ? '/.netlify/functions' : 'https://www.clothedeye.com/api';

    this.tokenKey = 'ce_auth_token';
    this.refreshTokenKey = 'ce_refresh_token';
    this.userKey = 'ce_user';

    logger.log(`🔐 AuthService initialized (${isDevelopment ? 'development' : 'production'})`);
  }

  /**
   * Sign up a new user
   */
  async signup(email, password, firstName = '', lastName = '') {
    try {
      logger.log('📝 Attempting user signup:', email);

      const response = await fetch(`${this.baseUrl}/auth-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Store tokens and user data
      this.setAuthData(data.token, data.refreshToken, data.user);

      logger.log('✅ User signup successful');
      return {
        success: true,
        user: data.user,
        requiresEmailVerification: data.requiresEmailVerification,
      };
    } catch (error) {
      logger.error('❌ Signup error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Log in an existing user
   */
  async login(email, password) {
    try {
      logger.log('🔐 Attempting login:', email);

      const response = await fetch(`${this.baseUrl}/auth-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store tokens and user data
      this.setAuthData(data.token, data.refreshToken, data.user);

      logger.log('✅ Login successful');
      return {
        success: true,
        user: data.user,
      };
    } catch (error) {
      logger.error('❌ Login error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Log out the current user
   */
  async logout() {
    try {
      const refreshToken = this.getRefreshToken();

      if (refreshToken) {
        // Call server to invalidate refresh token
        await fetch(`${this.baseUrl}/auth-logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
      }

      // Clear local storage
      this.clearAuthData();

      logger.log('✅ Logout successful');
      return { success: true };
    } catch (error) {
      logger.error('❌ Logout error:', error);
      // Still clear local data even if server call fails
      this.clearAuthData();
      return { success: true };
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken() {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${this.baseUrl}/auth-refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Token refresh failed');
      }

      // Update access token
      this.setToken(data.token);

      logger.log('✅ Access token refreshed');
      return {
        success: true,
        token: data.token,
      };
    } catch (error) {
      logger.error('❌ Token refresh error:', error);
      // Clear auth data on refresh failure
      this.clearAuthData();
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser() {
    try {
      const token = this.getToken();

      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${this.baseUrl}/auth-me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        // Try to refresh token if unauthorized
        if (response.status === 401) {
          const refreshResult = await this.refreshAccessToken();
          if (refreshResult.success) {
            // Retry with new token
            return this.getCurrentUser();
          }
        }
        throw new Error(data.error || 'Failed to get user');
      }

      // Update cached user data
      this.setUser(data.user);

      return {
        success: true,
        user: data.user,
      };
    } catch (error) {
      logger.error('❌ Get current user error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates) {
    try {
      const token = this.getToken();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${this.baseUrl}/auth-update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Profile update failed');
      }

      // Update cached user data
      this.setUser(data.user);

      logger.log('✅ Profile updated successfully');
      return {
        success: true,
        user: data.user,
      };
    } catch (error) {
      logger.error('❌ Profile update error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email) {
    try {
      const response = await fetch(`${this.baseUrl}/auth-request-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password reset request failed');
      }

      logger.log('✅ Password reset email sent');
      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      logger.error('❌ Password reset request error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${this.baseUrl}/auth-reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password reset failed');
      }

      logger.log('✅ Password reset successful');
      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      logger.error('❌ Password reset error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token) {
    try {
      const response = await fetch(`${this.baseUrl}/auth-verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Email verification failed');
      }

      // Update user data if authenticated
      if (this.isAuthenticated()) {
        await this.getCurrentUser();
      }

      logger.log('✅ Email verified successfully');
      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      logger.error('❌ Email verification error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ==========================================================================
  // LOCAL STORAGE HELPERS
  // ==========================================================================

  /**
   * Store authentication data
   */
  setAuthData(token, refreshToken, user) {
    localStorage.setItem(this.tokenKey, token);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * Clear authentication data
   */
  clearAuthData() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
  }

  /**
   * Get access token
   */
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Set access token
   */
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  /**
   * Get refresh token
   */
  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Get cached user data
   */
  getUser() {
    const userJson = localStorage.getItem(this.userKey);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch (error) {
      logger.error('Error parsing user data:', error);
      return null;
    }
  }

  /**
   * Set user data
   */
  setUser(user) {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Check if user is a subscriber
   */
  isSubscriber() {
    const user = this.getUser();
    if (!user) return false;

    const activeStatuses = ['active', 'trialing'];
    return activeStatuses.includes(user.subscription_status);
  }

  /**
   * Get subscription tier
   */
  getSubscriptionTier() {
    const user = this.getUser();
    return user?.subscription_tier || null;
  }

  /**
   * Check if user has admin privileges
   */
  isAdmin() {
    const user = this.getUser();
    // TODO: Add admin role field to users table
    // For now, check against known admin email
    return user?.email === 'admin@clothedeye.com';
  }

  /**
   * Make authenticated API request
   */
  async authenticatedRequest(url, options = {}) {
    const token = this.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle token expiration
    if (response.status === 401) {
      const refreshResult = await this.refreshAccessToken();
      if (refreshResult.success) {
        // Retry with new token
        headers['Authorization'] = `Bearer ${refreshResult.token}`;
        return fetch(url, { ...options, headers });
      } else {
        // Refresh failed, user needs to log in again
        this.clearAuthData();
        throw new Error('Authentication expired. Please log in again.');
      }
    }

    return response;
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
